import { Alert } from 'react-native';
import { format, isSameMonth, isThisYear, isToday, isTomorrow } from 'date-fns';
import { RRule } from 'rrule';
import { Tier } from 'features/premium/entities';
import { assertUnreachable } from 'utils/types';
export const getSign = (item) => {
    switch (item.type) {
        case 'DEPOSIT':
            return '+';
        case 'WITHDRAWAL':
            return '';
        default:
            if (item.amount.value > 0) {
                return '+';
            }
            return '';
    }
};
export const getPotEventName = (type) => {
    if (type) {
        switch (type) {
            case 'BASIC':
                return 'instant';
            case 'INTEREST':
                return 'easy_access';
            case 'INTEREST_NOTICE':
                return 'interest_notice';
            default:
                assertUnreachable(type);
                return '';
        }
    }
    return '';
};
export const sortPotProducts = (potProducts, potProductTierRateMap) => {
    potProducts.sort((a, b) => {
        const isARecommendedCashIsa = !!(a.isRecommended && a.isProductCashISA);
        const isBRecommendedCashIsa = !!(b.isRecommended && b.isProductCashISA);
        if (isARecommendedCashIsa && !isBRecommendedCashIsa)
            return -1;
        if (isBRecommendedCashIsa && !isARecommendedCashIsa)
            return 1;
        if (a.isRecommended && !b.isRecommended)
            return -1;
        if (b.isRecommended && !a.isRecommended)
            return 1;
        if (a.type === 'BASIC')
            return 1;
        if (b.type === 'BASIC')
            return -1;
        if (a.type === 'INTEREST_NOTICE' && b.type === 'INTEREST')
            return 1;
        if (b.type === 'INTEREST_NOTICE' && a.type === 'INTEREST')
            return -1;
        const interestForProductA = Number(potProductTierRateMap[a.id][Tier.ultimate]) || 0;
        const interestForProductB = Number(potProductTierRateMap[b.id][Tier.ultimate]) || 0;
        return interestForProductB - interestForProductA;
    });
};
export const showDeletedPotAlert = () => {
    Alert.alert('Hold on 💫', 'This pot is now closed, you can’t do much in here.');
};
export const showContinueInMobileAlert = () => {
    Alert.alert('Hold on 💫', 'Please continue in mobile');
};
export const formatPauseAutosaveDate = (date, withOn) => {
    if (isToday(date)) {
        return 'Today';
    }
    if (isTomorrow(date)) {
        return 'Tomorrow';
    }
    const formattedDate = format(date, `EEEE, 'the' do 'of' LLLL`);
    return withOn ? `on ${formattedDate}` : formattedDate;
};
export const formatAutosaveLastPaymentDate = (lastPaymentDate) => {
    const date = new Date(lastPaymentDate);
    if (isThisYear(date)) {
        return format(date, 'do LLL');
    }
    return format(date, "do LLL',' y");
};
export const formatNextCollectionDate = (dateStr) => {
    const date = new Date(dateStr);
    if (isToday(date)) {
        return 'Today';
    }
    if (isTomorrow(date)) {
        return 'Tomorrow';
    }
    const today = new Date();
    if (isSameMonth(date, today)) {
        return format(date, 'do eeee');
    }
    return format(date, 'do MMMM, eeee');
};
export const getPeriodFromRRule = (rrule) => {
    const parsed = RRule.parseString(rrule);
    if (parsed.freq === RRule.MONTHLY) {
        return 'Monthly';
    }
    return 'Weekly';
};
export const getSavingsPlanErrorInfo = (savingsPlanAccountStatus, bankName) => {
    switch (savingsPlanAccountStatus) {
        case 'CLOSED':
        case 'REMOVED':
            return 'Your funding account has been marked as closed, so autosaving is paused. Please reopen it to continue saving.';
        case 'CONNECTION_CLOSED':
            return 'Your funding account’s bank connection has been marked as closed, so autosaving is paused. Please reopen it to continue saving.';
        case 'DIRECT_DEBIT_CANCELLED':
            if (bankName) {
                return `Your Direct Debit has been cancelled by ${bankName}, so autosaving is paused. Please set up Direct Debit again to continue saving.`;
            }
            return 'Your Direct Debit has been cancelled by your bank, so autosaving is paused. Please set up Direct Debit again to continue saving.';
        case 'DIRECT_DEBIT_ACCOUNT_CHANGED':
            return 'Your funding account has changed, so autosaving is paused. Please set up Direct Debit again to continue.';
        case 'DELETED':
            return 'Your funding account has been disconnected, so autosaving is paused. Please reconnect it to continue saving.';
        case 'NEEDS_RECONSENT':
            return 'Your open banking consent has expired, so autosaving is paused. Please reconfirm your consent to continue.';
        case 'NEEDS_REAUTH':
            return 'Access to your funding account has been revoked. Reconnect your account to resume autosaving.';
        case 'DEACTIVATED_OVER_QUOTA':
            return 'Your funding account has been deactivated, so autosaving is paused. Please reactivate it to continue saving.';
        case 'DESTINATIONS_UNAVAILABLE':
            return 'We couldn’t collect your Autosave because none of your destinations could receive the deposit. Update your allocations before the next collection.';
        default:
            assertUnreachable(savingsPlanAccountStatus);
            return '';
    }
};
export const getSavingsPlanErrorButtonCTA = (actionRequired) => {
    switch (actionRequired) {
        case 'DIRECT_DEBIT_CANCELLED':
        case 'DIRECT_DEBIT_ACCOUNT_CHANGED':
            return 'Set up Direct Debit';
        case 'DELETED':
        case 'NEEDS_RECONSENT':
            return 'Renew connection';
        case 'CLOSED':
        case 'DEACTIVATED_OVER_QUOTA':
        case 'CONNECTION_CLOSED':
            return 'Manage connection';
        case 'NEEDS_REAUTH':
            return "Let's fix it";
        case 'REMOVED':
            return 'Contact support';
        case 'DESTINATIONS_UNAVAILABLE':
            return 'Update allocations';
        default:
            assertUnreachable(actionRequired);
            return 'Manage connection';
    }
};
export const getPotDisplayName = (name) => (name.toLowerCase().endsWith(` pot`) ? name : `${name} pot`);
